/**
 * Header the platform reads to learn which language to render anything it generates server-side
 * in. One of the `cs-` family, alongside `cs-organization` and `cs-fleet`, and named here for the
 * same reason `BD_ORGANIZATION_HEADER_NAME` is named in `ai-sidekick`: the packages that send it
 * share no owner for the constant.
 */
export const CS_LOCALE_HEADER_NAME = 'cs-locale';

/**
 * What a request says when nobody has said otherwise.
 *
 * This duplicates `REGIONAL_DEFAULTS.locale` in `@brightdrop/localization-core`, which is where
 * the answer properly lives. It cannot be imported: the core is a module-federation singleton
 * shared by one app, and this package is bundled into four, so depending on it here would give
 * three of them a second copy of the core. A test in `fleet-management`, which depends on both,
 * holds the two equal.
 */
const DEFAULT_LOCALE = 'en-US';

let requestLocale: string = DEFAULT_LOCALE;

/**
 * Records the locale outbound requests should speak.
 *
 * Module-level rather than React context because the callers are `prepareHeaders` functions and an
 * Axios interceptor, none of which runs inside the component tree — the same reason the bearer
 * token is reached through `refreshTokenHandler` rather than a hook. It also has to be a single
 * instance across the federation, and this package is the only one every base query already shares
 * as a singleton.
 *
 * Only the emptiness of a value is judged here. What a locale tag may contain is the resolver's
 * question in `localization-core`, which has already answered it by the time anything reaches this
 * module; re-deciding it with a second, poorer rule is how the two come to disagree.
 *
 * @param locale - The reader's resolved locale, or `undefined` to return to the default.
 */
export const setRequestLocale = (locale: string | undefined): void => {
  requestLocale = locale?.trim() || DEFAULT_LOCALE;
};

/**
 * The locale at request time rather than at render time, so a request issued while a reader was
 * still being loaded speaks for who they were then.
 */
export const getRequestLocale = (): string => requestLocale;

/**
 * Writes the header onto the mutable `Headers` RTK Query hands to `prepareHeaders`.
 *
 * Overwrites rather than defers to a caller, so this module stays the only answer to the question.
 * A call site setting the header itself would be speaking for a reader it cannot see, and unlike
 * the other `cs-` headers there is no per-request reason to: every request from one session is
 * read by the same person.
 */
export const setLocaleHeader = (headers: Headers): void => {
  headers.set(CS_LOCALE_HEADER_NAME, getRequestLocale());
};
